import {Button, Form, Input, Modal, Popconfirm, Select, Table} from 'antd';
import {DeleteOutlined, UserAddOutlined} from '@ant-design/icons';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../../common/hooks';
import {
  searchBy,
  pageNumberChange,
  pageSizeChange,
  updateStationGroup,
  stationGroupStations,
  sortBy,
} from '../stationGroupsSlice';
import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {clearStationsFilters, stationsList} from '../../stations/stationsSlice';
import type {STATION_GROUP_STATIONS} from '../stationGroups.type';
import {debounce} from 'lodash';
import {debounceDelay, sortbyFilter} from '../../../../../common/constants';

const Main = ({groupId}: {groupId: number}) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.stationGroups);
  const [addStationModalOpen, setAddStationModelOpen] = useState(false);
  const userPermission = useAppSelector((state) => state.config.config.permissions);
  const stationsOption = useAppSelector((state) => state.stations.stations.pageData);
  const [addStationForm] = Form.useForm();
  const {Option} = Select;

  const handleFilterSearch = (e: string) => {
    dispatch(searchBy(e));
  };

  const handleStationBouncing = debounce((e) => {
    dispatch(stationsList({search: e, is_active: true}));
  }, debounceDelay);

  const handleAddStationModalClose = () => {
    addStationForm.resetFields();
    setAddStationModelOpen(false);
    dispatch(clearStationsFilters());
  };

  const handleAddStation = ({station_ids}: {station_ids: number[]}) => {
    station_ids = station_ids.map(Number);
    const temp = state.stationGroupStations.pageData.map((x) => x.id);
    station_ids = [...station_ids, ...temp];
    dispatch(updateStationGroup({groupId: groupId, payload: {station_ids: station_ids}}))
      .unwrap()
      .then(() => {
        dispatch(
          stationGroupStations({
            groupId: groupId,
            filters: {
              page_size: state.stationGroupStations.page_size,
              page_number: state.stationGroupStations.page_number,
              sort_by: state.stationGroupStations.sort_by,
              ascending: state.stationGroupStations.ascending,
              search: state.stationGroupStations.search,
            },
          }),
        );
        handleAddStationModalClose();
      });
  };
  const handleRemoveStation = (stationId: number) => {
    const stations = state.stationGroupStations.pageData.map((x) => x.id);
    const payload = stations.filter((x) => x != stationId);
    dispatch(updateStationGroup({groupId: groupId, payload: {station_ids: payload}}))
      .unwrap()
      .then(() => {
        dispatch(
          stationGroupStations({
            groupId: groupId,
            filters: {
              page_size: state.stationGroupStations.page_size,
              page_number: state.stationGroupStations.page_number,
              sort_by: state.stationGroupStations.sort_by,
              ascending: state.stationGroupStations.ascending,
              search: state.stationGroupStations.search,
            },
          }),
        );
      });
  };
  const columns: ColumnsType<STATION_GROUP_STATIONS> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return (
          <>
            {(state.stationGroupStations.page_number - 1) * state.stationGroupStations.page_size +
              index +
              1}
          </>
        );
      },
    },
    {
      title: 'Station Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'Address',
      dataIndex: 'address',
    },

    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            {userPermission.update_stationgroup && (
              <Popconfirm
                title='Are you sure '
                onConfirm={() => {
                  handleRemoveStation(record.id);
                }}
                okText='Yes'
                cancelText='No'
              >
                <DeleteOutlined
                  style={{cursor: 'pointer', fontSize: '16px', color: 'red', marginLeft: '10px'}}
                />
              </Popconfirm>
            )}
          </>
        );
      },
    },
  ];
  !userPermission.update_permissiongroup ? (columns.length = 2) : null;

  useEffect(() => {
    dispatch(
      stationGroupStations({
        groupId: groupId,
        filters: {
          page_size: state.stationGroupStations.page_size,
          page_number: state.stationGroupStations.page_number,
          sort_by: state.stationGroupStations.sort_by,
          ascending: state.stationGroupStations.ascending,
          search: state.stationGroupStations.search,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.stationGroupStations.page_number,
    state.stationGroupStations.page_size,
    state.stationGroupStations.search,
    state.stationGroupStations.sort_by,
    state.stationGroupStations.ascending,
  ]);
  return (
    <>
      <div>
        <div
          style={{
            margin: '10px 0px 10px 0px',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '350px 1fr',
          }}
        >
          <Input.Search
            placeholder='Search'
            defaultValue={state.stationGroupStations.search}
            style={{borderRadius: '10px'}}
            allowClear
            onSearch={(e) => handleFilterSearch(e)}
          />
          {userPermission.update_stationgroup && (
            <div style={{width: '100%', textAlign: 'end'}}>
              <Button
                type='primary'
                style={{borderRadius: '5px', marginInline: '5px'}}
                icon={<UserAddOutlined />}
                onClick={() => {
                  dispatch(stationsList({is_active: true}));
                  setAddStationModelOpen(true);
                }}
              >
                Add Station
              </Button>
            </div>
          )}
        </div>
        <Table
          rowKey='id'
          columns={columns}
          pagination={
            {
              total: state.stationGroupStations.totalDataCount,

              current: state.stationGroupStations.page_number,
              pageSize: state.stationGroupStations.page_size,
              showSizeChanger: true,
            } as TablePaginationConfig
          }
          loading={state.isLoading}
          size='middle'
          dataSource={state.stationGroupStations.pageData}
          scroll={{x: undefined, y: '60vh'}}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={(pagination, _, {order, field}: any) => {
            if (
              pagination.pageSize &&
              pagination.pageSize !== state.stationGroupStations.page_size
            ) {
              dispatch(pageSizeChange(pagination.pageSize));
            }
            if (
              pagination.current &&
              pagination.current !== state.stationGroupStations.page_number
            ) {
              dispatch(pageNumberChange(pagination.current));
            }
            if (field) {
              if (order === sortbyFilter.DESCEND) {
                dispatch(sortBy({ascending: false, sortBy: field}));
              } else {
                dispatch(sortBy({ascending: true, sortBy: field}));
              }
            }
          }}
        />
      </div>
      <Modal
        title='Add Station In Station Group'
        open={addStationModalOpen}
        onCancel={() => {
          handleAddStationModalClose();
        }}
        okText={'Add'}
        maskClosable={false}
        style={{height: '700px', width: '800px'}}
        onOk={() => {
          addStationForm
            .validateFields()
            .then((values) => {
              handleAddStation(values);
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Form form={addStationForm} name='Add Station' layout='vertical'>
          <Form.Item
            label='Stations'
            name='station_ids'
            rules={[{required: true, message: 'At least one Employee is required'}]}
          >
            <Select
              showSearch
              mode='multiple'
              placeholder='Search Station'
              defaultActiveFirstOption={false}
              showArrow={false}
              filterOption={false}
              onSearch={(e) => {
                if (e.length > 3) handleStationBouncing(e);
              }}
              notFoundContent={null}
              //loading={}
            >
              {stationsOption.map((x) => (
                <Option key={x.id}>{x.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Main;
